// Operator › Templates › a template (`/operator/templates/$templateId`; DC-03): the template head,
// its versions newest first with their state and which one is current
// (`GET /v1/operator/agent-templates/{id}`), and its learning-loop edit proposals, read only
// (`GET /v1/operator/template-edit-proposals?template_id=`). Two calls on load. Template
// authors cut a new draft version here; a version opens on its own page.
import {
  Badge,
  Button,
  LocationHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../../api/client";
import type { AgentTemplateVersion, MeResponse, TemplateEditProposal } from "../../../api/types";
import { formatDateTime } from "../../format";
import { OpsDoc, OpsFrame, operatorCrumb } from "../../ops-ux";
import { Facts, LoadError, Loading, Nothing, Section } from "../../screen-parts";
import {
  AuthorOnlyNote,
  CLASS_LABEL,
  isTemplateAuthor,
  ProposalStateBadge,
  SCOPE_LABEL,
  SOURCE_LABEL,
  TemplateStatusBadge,
  VersionStateBadge,
  versionLabel,
} from "./template-parts";
import { agentTemplateQuery, templateKeys, templateProposalsQuery } from "./template-queries";
import { VersionFormDialog } from "./version-form";

export function TemplateDetail({
  api,
  me,
  templateId,
}: {
  api: Api;
  me: MeResponse;
  templateId: string;
}) {
  const detail = useQuery(agentTemplateQuery(api, templateId));
  const proposals = useQuery(templateProposalsQuery(api, templateId));
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [drafting, setDrafting] = useState(false);
  const author = isTemplateAuthor(me);
  const crumbs = [operatorCrumb, { label: "Templates", render: <Link to="/operator/templates" /> }];

  if (detail.isPending)
    return (
      <OpsFrame>
        <LocationHeader crumbs={crumbs} title="Template" />
        <OpsDoc>
          <Loading label="Loading the template" />
        </OpsDoc>
      </OpsFrame>
    );
  if (detail.isError)
    return (
      <OpsFrame>
        <LocationHeader crumbs={crumbs} title="Template" />
        <OpsDoc>
          <LoadError what="The template" error={detail.error} />
        </OpsDoc>
      </OpsFrame>
    );

  const { template: t, versions } = detail.data;
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={crumbs}
        title={t.name}
        badge={<TemplateStatusBadge status={t.status} />}
        actions={
          author ? (
            <Button size="sm" onClick={() => setDrafting(true)}>
              <PlusIcon aria-hidden="true" />
              New draft version
            </Button>
          ) : undefined
        }
      />
      <OpsDoc>
        <p className="mb-4 text-xs text-muted-foreground" data-slot="template-facts">
          {SCOPE_LABEL[t.scope]} template · {CLASS_LABEL[t.template_class]} · {t.kind} ·{" "}
          <code>{t.key}</code>
        </p>
        {author ? null : <AuthorOnlyNote />}
        <Section id="template-facts" title="Template">
          <Facts
            items={[
              { label: "Key", value: <code className="text-xs">{t.key}</code> },
              { label: "Scope", value: SCOPE_LABEL[t.scope] },
              { label: "Class", value: CLASS_LABEL[t.template_class] },
              { label: "Kind", value: t.kind === "persistent" ? "Persistent (a DE)" : "Ephemeral" },
              {
                label: "Current version",
                value: t.current_version_id ? (
                  <Link
                    to="/operator/templates/$templateId/versions/$versionId"
                    params={{ templateId: t.id, versionId: t.current_version_id }}
                    className="underline-offset-4 hover:underline"
                  >
                    {versionLabel(versions, t.current_version_id)}
                  </Link>
                ) : (
                  "None published"
                ),
              },
              { label: "Created", value: formatDateTime(t.created_at) },
            ]}
          />
        </Section>
        <Section
          id="template-versions"
          title="Versions"
          description="A draft is editable until it validates; a validated version is frozen and can be published."
        >
          {versions.length === 0 ? (
            <Nothing title="No versions yet">
              {author ? "Cut the first draft with “New draft version”." : null}
            </Nothing>
          ) : (
            <VersionsTable templateId={t.id} versions={versions} current={t.current_version_id} />
          )}
        </Section>
        <Section
          id="template-proposals"
          title="Edit proposals"
          description="Learning-loop edits proposed for this template, each with the eval it ships with."
        >
          {proposals.isPending ? (
            <Loading label="Loading edit proposals" rows={2} />
          ) : proposals.isError ? (
            <LoadError what="Edit proposals" error={proposals.error} />
          ) : (
            <ProposalsTable proposals={proposals.data.data} versions={versions} />
          )}
        </Section>
        {author ? (
          <VersionFormDialog
            mode="create"
            api={api}
            open={drafting}
            onOpenChange={setDrafting}
            templateId={t.id}
            versions={versions}
            supersedes={t.current_version_id ?? versions[0]?.id ?? null}
            onSaved={async (created) => {
              qc.setQueryData(templateKeys.version(created.version.id), created);
              await qc.invalidateQueries({ queryKey: templateKeys.all() });
              setDrafting(false);
              void navigate({
                to: "/operator/templates/$templateId/versions/$versionId",
                params: { templateId: t.id, versionId: created.version.id },
              });
            }}
          />
        ) : null}
      </OpsDoc>
    </OpsFrame>
  );
}

function VersionsTable({
  templateId,
  versions,
  current,
}: {
  templateId: string;
  versions: AgentTemplateVersion[];
  current: string | null;
}) {
  return (
    <Table data-slot="template-versions">
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Version</TableHead>
          <TableHead scope="col">State</TableHead>
          <TableHead scope="col">Source</TableHead>
          <TableHead scope="col">Changelog</TableHead>
          <TableHead scope="col">Updated</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {versions.map((v) => (
          <TableRow key={v.id} data-version={v.version}>
            <TableHead scope="row" className="font-normal">
              <Link
                to="/operator/templates/$templateId/versions/$versionId"
                params={{ templateId, versionId: v.id }}
                className="font-medium underline-offset-4 hover:underline"
              >
                v{v.version}
              </Link>
            </TableHead>
            <TableCell>
              <span className="flex flex-wrap items-center gap-1.5">
                <VersionStateBadge state={v.state} />
                {v.id === current ? <Badge variant="accent">Current</Badge> : null}
              </span>
            </TableCell>
            <TableCell>{SOURCE_LABEL[v.source]}</TableCell>
            <TableCell className="max-w-md text-muted-foreground">{v.changelog ?? "—"}</TableCell>
            <TableCell className="text-muted-foreground">{formatDateTime(v.updated_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function ProposalsTable({
  proposals,
  versions,
}: {
  proposals: TemplateEditProposal[];
  versions: AgentTemplateVersion[];
}) {
  if (proposals.length === 0) return <Nothing title="No edit proposals" />;
  return (
    <Table data-slot="template-proposals">
      <TableHeader>
        <TableRow>
          <TableHead scope="col">State</TableHead>
          <TableHead scope="col">Source</TableHead>
          <TableHead scope="col">Based on</TableHead>
          <TableHead scope="col">Eval</TableHead>
          <TableHead scope="col">Diff</TableHead>
          <TableHead scope="col">Reason</TableHead>
          <TableHead scope="col">Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {proposals.map((p) => (
          <TableRow key={p.id} data-proposal={p.id}>
            <TableCell>
              <ProposalStateBadge state={p.state} />
            </TableCell>
            <TableCell>{p.source === "reflection" ? "Reflection" : "A person"}</TableCell>
            <TableCell>{versionLabel(versions, p.based_on_version_id)}</TableCell>
            <TableCell>
              <code className="text-xs">…{p.eval_id.slice(-6)}</code>
            </TableCell>
            <TableCell className="max-w-xs break-all">
              <code className="text-xs">{p.diff_ref}</code>
            </TableCell>
            <TableCell className="text-muted-foreground">{p.reason ?? "—"}</TableCell>
            <TableCell className="text-muted-foreground">{formatDateTime(p.created_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
